import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Button, Divider, Drawer, Grid, IconButton, Paper, TextField, Typography, Autocomplete,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  InputAdornment,
  Alert
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BedroomParentIcon from '@mui/icons-material/BedroomParent';
import GroupsIcon from '@mui/icons-material/Groups';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { getAllHotels } from '../../api/hotelsAPI';
import { getAllVehicle } from '../../api/vehicleAPI';
import { addChangeRequest, addChangeRequestForItineray, getQueriesByoperation, getSingleOperation, updateFollowupDetails, GuestandTripUpdateByOpt } from '../../api/operationAPI';
import { calculateSingleHotelCost, exportQueryViewPDF, mapOperationToPdfData } from '../createItinerary/createInquiryCalculation';
import { getPaymentSchedule } from '../../api/paymentScheduleAPI';
import useSnackbar from '../../hooks/useSnackbar';
import usePermissions from '../../hooks/UsePermissions';

const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  if (includeTime) {
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (val) => {
  const num = Number(val);
  if (isNaN(num)) return '₹0';
  return `₹${num.toLocaleString('en-IN')}`;
};

const getStatusChip = (status) => {
  const s = typeof status === 'string' ? status : (status?.status || status?.label || '');
  switch (s.toLowerCase()) {
    case 'approved':
    case 'confirm':
    case 'confirmed':
      return <Chip label={s || 'Confirmed'} color="success" size="small" icon={<VerifiedIcon />} variant="filled" sx={{ fontWeight: 'bold' }} />;
    case 'rejected':
    case 'cancelled':
      return <Chip label={s || 'Rejected'} color="error" size="small" icon={<ErrorOutlineIcon />} variant="filled" sx={{ fontWeight: 'bold' }} />;
    case 'pending':
    case 'upcoming':
      return <Chip label={s || 'Pending'} color="warning" size="small" icon={<PendingActionsIcon />} variant="filled" sx={{ fontWeight: 'bold' }} />;
    case 'change request':
    case 'change-request':
      return <Chip label="Change Request" color="info" size="small" icon={<EditOutlinedIcon />} variant="filled" sx={{ fontWeight: 'bold' }} />;
    default:
      return <Chip label={s || 'N/A'} size="small" variant="outlined" sx={{ fontWeight: '600' }} />;
  }
};

const getMealPlanLabel = (plan) => {
  const plans = {
    'cp_plan': 'CP (Breakfast)',
    'map_plan': 'MAP (Bf + 1 Meal)',
    'ap_plan': 'AP (All Meals)',
    'ep_plan': 'EP (No Meals)'
  };
  return plans[plan] || plan || '-';
};

const columnsData = [
  { label: 'Day', field: 'day' },
  { label: 'Date', field: 'date' },
  { label: 'Place', field: 'place' },
  { label: 'Hotel Name', field: 'hotelName' },
  { label: 'Hotel Amount', field: 'hotelAmount' },
  { label: 'Hotel Confirmation', field: 'hotelConfirmation' },
  { label: 'Checkin Date', field: 'checkinDate' },
  { label: 'Checkout Date', field: 'checkoutDate' },
  { label: 'Room Type', field: 'roomType' },
  { label: 'Meal Plan', field: 'mealPlan' },
  { label: 'Vehicle Name', field: 'vehicleName' },
  { label: 'Vehicle Payment', field: 'vehiclePayment' },
  { label: 'Vehicle Status', field: 'vehicleStatus' },
  { label: 'Driver Name', field: 'driverName' },
  { label: 'CRV', field: 'status' }
];

const SingleQueriesView = () => {
  const navigate = useNavigate();
  const resolveValidId = (rawId) => {
    if (!rawId || typeof rawId !== 'string') return rawId;
    const trimmed = rawId.trim();
    if (trimmed.length === 25) {
      for (let i = 0; i < trimmed.length - 1; i++) {
        if (trimmed[i] === trimmed[i + 1]) {
          const candidate = trimmed.slice(0, i) + trimmed.slice(i + 1);
          if (/^[0-9a-fA-F]{24}$/.test(candidate)) return candidate;
        }
      }
    }
    return trimmed;
  };

  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const queryParamId = searchParams.get('id');
  const urlId = resolveValidId(paramId || queryParamId);

  const { fetchSelectedquerie } = useSelector((state) => state.queries || {});
  const userRole = useSelector((state) => state.tokens?.user?.permission);
  const hasPermission = usePermissions();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // Role permissions: allow any authorized staff to edit cards
  const canEditCards = hasPermission("operation", "alter") || 
                       hasPermission("queries", "alter") || 
                       ['Admin', 'Admin-a', 'Operation Manager', 'Sales Manager', 'Sales Team Lead', 'Super Admin'].includes(userRole);

  const [hotels, setHotels] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);

  const [operationRaw, setOperationRaw] = useState(null);
  const [queryRaw, setQueryRaw] = useState(null);
  const [guestId, setGuestId] = useState(null);
  const [operationId, setOperationId] = useState(null);

  const [itineraryData, setItineraryData] = useState([]);
  const [guestInfo, setGuestInfo] = useState({});
  const [stayInfo, setStayInfo] = useState({ rooms: 0, hotel: '' });
  const [tripDetails, setTripDetails] = useState({ pax: 0, kids_above_5: 0, duration: 0 });
  const [hotelSeason, setHotelSeason] = useState('off_season_price');

  // Edit Itinerary Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [itinerarySubmitting, setItinerarySubmitting] = useState(false);

  // Edit Guest Drawer State (Isolated from view state)
  const [guestDrawer, setGuestDrawer] = useState(false);
  const [guestEditData, setGuestEditData] = useState({
    name: '',
    country_code: '+91',
    contact: '',
    email: '',
    pax: 2,
    kids_above_5: 0,
    hotel: '',
    roomType: '',
    rooms: 1,
    carname: '',
    carcount: 1,
  });
  const [guestSubmitting, setGuestSubmitting] = useState(false);

  // Edit Booking Drawer State (Isolated from view state)
  const [bookingDrawer, setBookingDrawer] = useState(false);
  const [bookingEditData, setBookingEditData] = useState({
    source: 'website',
    tourDate: '',
    bookingStatus: 'Confirm',
    cost: '',
    advancePayment: '',
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  // Dialog states
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [verifyPopupOpen, setVerifyPopupOpen] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState('');
  const [rejectedReason, setRejectedReason] = useState('');
  const [rejectedViewOpen, setRejectedViewOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);

  const targetId = urlId || fetchSelectedquerie?.operation_id || fetchSelectedquerie?.id || fetchSelectedquerie?._id;

  const fetchOperationData = useCallback(async () => {
    const currentId = urlId || fetchSelectedquerie?.operation_id || fetchSelectedquerie?.id || fetchSelectedquerie?._id;
    if (!currentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      let operationData = null;
      try {
        operationData = await getSingleOperation(currentId);
      } catch (err) {
        console.warn("Could not fetch operation by id directly:", err);
      }

      const opId = operationData?._id || currentId;
      setOperationId(opId);
      setOperationRaw(operationData);

      let queriesDataRaw = null;
      try {
        queriesDataRaw = await getQueriesByoperation(opId);
      } catch (err) {
        console.warn("Could not fetch queries by operation id:", err);
      }

      const queriesData = Array.isArray(queriesDataRaw)
        ? (queriesDataRaw[0] || {})
        : (queriesDataRaw || (fetchSelectedquerie?._id ? fetchSelectedquerie : {}));
      setQueryRaw(queriesData);

      // Determine the true guest ID
      const resolvedGuestId = operationData?.guest_id?._id || 
                              operationData?.guest_id || 
                              queriesData?.guest_id?._id || 
                              queriesData?.guest_id || 
                              fetchSelectedquerie?.guest_id;
      setGuestId(resolvedGuestId);

      const { guestInfo: mappedGuestInfo, itineraryData: mappedItineraryData } = mapOperationToPdfData(
        operationData,
        queriesData,
        fetchSelectedquerie?.guest_info
      );

      // Calculate Total Paid and Due Amount from Payment Schedule if it exists
      let totalPaid = Number(mappedGuestInfo.advancePayment) || 0;
      let totalCost = Number(mappedGuestInfo.cost) || 0;
      try {
        const scheduleId = queriesData?._id || opId;
        if (scheduleId) {
          const scheduleRes = await getPaymentSchedule(scheduleId);
          if (scheduleRes && scheduleRes.data) {
            const schedule = scheduleRes.data;
            const instSum = (schedule.installments || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
            const psTotal = Number(schedule.total_amount) || 0;
            const psAdv = Number(schedule.advance_payment) || 0;

            if (psTotal > 0 && (totalCost === 0 || Math.abs(psTotal - (psAdv + instSum)) < 1 || Math.abs(psTotal - totalCost) < 1)) {
              totalCost = psTotal;
            }
            if (psAdv > 0 && (Math.abs(psTotal - (psAdv + instSum)) < 1 || totalPaid === 0)) {
              totalPaid = psAdv;
            }

            if (schedule.installments && schedule.installments.length > 0) {
              schedule.installments.forEach(inst => {
                if (inst.status === 'Paid') {
                  totalPaid += (Number(inst.amount) || 0);
                }
              });
            }
          }
        }
      } catch (e) {
        // Payment schedule may not exist yet
      }
      mappedGuestInfo.totalPaidAmount = totalPaid;
      mappedGuestInfo.duePayment = Math.max(0, totalCost - totalPaid);

      setItineraryData(mappedItineraryData);
      setGuestInfo(mappedGuestInfo);
      setTripDetails({
        pax: operationData?.trip_details?.number_of_adults || operationData?.trip_details?.pax || queriesData?.pax || 0,
        kids_above_5: operationData?.trip_details?.number_of_kids || queriesData?.kids_above_5 || 0,
        duration: operationData?.trip_details?.duration || queriesData?.duration || mappedItineraryData.length || 0,
      });
      setStayInfo({
        hotel: mappedGuestInfo.hotel || queriesData?.stay_info?.hotel || operationData?.trip_details?.accommodation || '',
        rooms: Number(mappedGuestInfo.rooms) || queriesData?.stay_info?.rooms || operationData?.trip_details?.rooms || 1,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching operation data:", error);
      if (fetchSelectedquerie && fetchSelectedquerie._id) {
        const { guestInfo: mappedGuestInfo, itineraryData: mappedItineraryData } = mapOperationToPdfData(
          null,
          fetchSelectedquerie,
          fetchSelectedquerie.guest_info
        );
        setItineraryData(mappedItineraryData);
        setGuestInfo(mappedGuestInfo);
        setTripDetails({
          pax: fetchSelectedquerie.pax || 0,
          kids_above_5: fetchSelectedquerie.kids_above_5 || 0,
          duration: fetchSelectedquerie.duration || mappedItineraryData.length || 0,
        });
        setStayInfo({
          hotel: fetchSelectedquerie.stay_info?.hotel || '',
          rooms: fetchSelectedquerie.stay_info?.rooms || 1,
        });
      } else {
        setError("Failed to load query details. Please try again.");
        showSnackbar("Failed to fetch operation data", "error");
      }
      setLoading(false);
    }
  }, [urlId, fetchSelectedquerie, showSnackbar]);

  useEffect(() => {
    fetchOperationData();
    getAllHotels().then(setHotels).catch(() => {});
    getAllVehicle().then(setVehicles).catch(() => {});
  }, [fetchOperationData]);

  // Open Guest Edit Drawer
  const openGuestEditDrawer = () => {
    setGuestEditData({
      name: guestInfo.name || '',
      country_code: guestInfo.country_code || '+91',
      contact: guestInfo.contact || '',
      email: guestInfo.email || '',
      pax: guestInfo.pax || 2,
      kids_above_5: guestInfo.kids_above_5 || 0,
      hotel: guestInfo.hotel || '',
      roomType: guestInfo.roomType || '',
      rooms: guestInfo.rooms || 1,
      carname: guestInfo.carname || '',
      carcount: guestInfo.carcount || 1,
    });
    setGuestDrawer(true);
  };

  // Save Guest Information
  const saveGuestInfo = async () => {
    try {
      setGuestSubmitting(true);
      const targetGuestId = guestId || 
                            operationRaw?.guest_id?._id || 
                            operationRaw?.guest_id || 
                            queryRaw?.guest_id?._id || 
                            queryRaw?.guest_id || 
                            '000000000000000000000000';
      const targetOpId = operationId || targetId;

      await GuestandTripUpdateByOpt(targetGuestId, targetOpId, guestEditData);

      // Optimistic local state update
      setGuestInfo(prev => ({
        ...prev,
        name: guestEditData.name,
        country_code: guestEditData.country_code,
        contact: guestEditData.contact,
        email: guestEditData.email,
        pax: guestEditData.pax,
        kids_above_5: guestEditData.kids_above_5,
        hotel: guestEditData.hotel,
        roomType: guestEditData.roomType,
        rooms: guestEditData.rooms,
        carname: guestEditData.carname,
        carcount: guestEditData.carcount,
      }));

      setStayInfo(prev => ({
        ...prev,
        hotel: guestEditData.hotel,
        rooms: Number(guestEditData.rooms) || 1,
      }));

      setGuestDrawer(false);
      showSnackbar("Guest & Stay Information updated successfully", "success");
      await fetchOperationData();
    } catch (err) {
      console.error("Error updating guest info:", err);
      showSnackbar(err.response?.data?.message || "Failed to update guest information", "error");
    } finally {
      setGuestSubmitting(false);
    }
  };

  // Open Booking Edit Drawer
  const openBookingEditDrawer = () => {
    setBookingEditData({
      source: guestInfo.source || 'website',
      tourDate: guestInfo.tourDate ? guestInfo.tourDate.slice(0, 10) : '',
      bookingStatus: guestInfo.bookingStatus || 'Confirm',
      cost: guestInfo.cost || 0,
      advancePayment: guestInfo.advancePayment || guestInfo.totalPaidAmount || 0,
    });
    setBookingDrawer(true);
  };

  // Save Booking & Financials
  const saveBookingInfo = async () => {
    try {
      setBookingSubmitting(true);
      const targetGuestId = guestId || 
                            operationRaw?.guest_id?._id || 
                            operationRaw?.guest_id || 
                            queryRaw?.guest_id?._id || 
                            queryRaw?.guest_id || 
                            '000000000000000000000000';
      const targetOpId = operationId || targetId;

      const newCost = Number(bookingEditData.cost) || 0;
      const newAdvance = Number(bookingEditData.advancePayment) || 0;

      await GuestandTripUpdateByOpt(targetGuestId, targetOpId, {
        source: bookingEditData.source,
        tourDate: bookingEditData.tourDate,
        bookingStatus: bookingEditData.bookingStatus,
        cost: newCost,
        advancePayment: newAdvance,
        advance: newAdvance,
      });

      // Optimistic local state update
      setGuestInfo(prev => ({
        ...prev,
        source: bookingEditData.source,
        tourDate: bookingEditData.tourDate,
        bookingStatus: bookingEditData.bookingStatus,
        cost: newCost,
        advancePayment: newAdvance,
        totalPaidAmount: newAdvance,
        duePayment: Math.max(0, newCost - newAdvance),
      }));

      setBookingDrawer(false);
      showSnackbar("Booking details updated successfully", "success");
      await fetchOperationData();
    } catch (err) {
      console.error("Error updating booking info:", err);
      showSnackbar(err.response?.data?.message || "Failed to update booking details", "error");
    } finally {
      setBookingSubmitting(false);
    }
  };

  // Open Itinerary Day Drawer
  const openEditDrawer = useCallback((row, index) => {
    setEditRow({ ...row });
    setEditIndex(index);
    setDrawerOpen(true);

    if (hotels && hotels.length > 0) {
      const selectedHotel = hotels.find(h => h.hotel_name === row.hotelName || h._id === row.hotelId);
      setRoomOptions(selectedHotel?.category || []);
    }

    if (vehicles && vehicles.length > 0) {
      const owner = vehicles.find(v => v.vehicles?.some(veh => veh.vehicle_name === row.vehicleName));
      setDriverOptions(owner?.drivers || []);
    }
  }, [hotels, vehicles]);

  const hasHotelPrice = (hotelId) => {
    if (!hotelSeason) return false;
    const hotel = hotels.find(h => h._id === hotelId);
    if (!hotel) return false;
    return hotel.category?.some(cat => {
      const pricing = cat[hotelSeason];
      if (!pricing) return false;
      return ['cp_plan', 'map_plan', 'ap_plan'].some(plan => pricing[plan] > 0);
    });
  };

  const handleEditChange = (e) => {
    setEditRow(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const mapItineraryToPayload = (data) => {
    return data.map(item => ({
      _id: item.id,
      journey_date: item.date,
      destination: item.place,
      hotel_name: item.hotelName,
      hotel_amount: Number(item.hotelAmount) || 0,
      hotel_confirmation: item.hotelConfirmation,
      checkin_date: item.checkinDate,
      checkout_date: item.checkoutDate,
      meal_plan: item.mealPlan,
      room_type: item.roomType,
      hotel_id: item.hotelId,
      vehicle_name: item.vehicleName,
      vehicle_payment: Number(item.vehiclePayment) || 0,
      vehicle_status: item.vehicleStatus,
      driver: item.driverName,
      approved_status: item.status || 'Pending',
      rejected_reason: item.rejected_reason || []
    }));
  };

  const saveEdit = async () => {
    try {
      if (editIndex === null || !itineraryData[editIndex]) {
        showSnackbar("Error: Invalid edit state", "error");
        return;
      }

      setItinerarySubmitting(true);
      const newData = [...itineraryData];
      const updatedRow = { ...editRow };

      if (hasPermission("operation", "alter") || canEditCards) {
        newData[editIndex] = updatedRow;
        const payload = mapItineraryToPayload(newData);
        const res = await updateFollowupDetails(operationId || targetId, { followup_details: payload });

        if (res) {
          showSnackbar(res.message || "Itinerary day updated successfully", "success");
          setItineraryData(newData);
          setDrawerOpen(false);
          await fetchOperationData();
        }
      } else if (hasPermission("operation", "change-request")) {
        const changeDescription = `Day ${updatedRow.day} changes for ${updatedRow.place || 'Itinerary'}: Hotel: ${updatedRow.hotelName || '-'}, Vehicle: ${updatedRow.vehicleName || '-'}`;
        const res = await addChangeRequestForItineray(operationId || targetId, { description: changeDescription });

        if (res) {
          showSnackbar("Change request submitted successfully", "success");
          setDrawerOpen(false);
        }
      } else {
        showSnackbar("Permission denied to alter this itinerary", "error");
      }
    } catch (error) {
      console.error("Error in saveEdit:", error);
      showSnackbar(error.response?.data?.message || "Failed to save itinerary changes", "error");
    } finally {
      setItinerarySubmitting(false);
    }
  };

  // Auto-update hotel cost when season/room/meal changes in drawer
  useEffect(() => {
    if (drawerOpen && editRow.hotelId && editRow.roomType && editRow.mealPlan) {
      const cost = calculateSingleHotelCost(
        { hotelId: editRow.hotelId, roomType: editRow.roomType, mealPlan: editRow.mealPlan },
        hotels,
        hotelSeason,
        tripDetails,
        stayInfo
      );
      if (cost && cost !== editRow.hotelAmount) {
        setEditRow(prev => ({ ...prev, hotelAmount: cost }));
      }
    }
  }, [hotelSeason, drawerOpen]);

  const handleAddExtraDay = async () => {
    try {
      if (!itineraryData || itineraryData.length === 0) {
        showSnackbar("No existing itinerary found to append to.", "error");
        return;
      }
      
      const lastDay = itineraryData[itineraryData.length - 1];
      const nextDate = new Date(lastDay.date || new Date());
      nextDate.setDate(nextDate.getDate() + 1);

      const nextDayStr = nextDate.toISOString().split('T')[0];
      const dayAfterStr = new Date(nextDate.getTime() + 86400000).toISOString().split('T')[0];

      const newDay = {
        id: undefined,
        day: (itineraryData.length + 1).toString(),
        date: nextDayStr,
        place: '',
        hotelName: '',
        hotelAmount: 0,
        hotelConfirmation: '',
        checkinDate: nextDayStr,
        checkoutDate: dayAfterStr,
        mealPlan: '',
        vehicleName: '',
        vehiclePayment: 0,
        vehicleStatus: 'Pending',
        driverName: '',
        hotelId: '',
        roomType: '',
        status: 'Pending',
        rejected_reason: []
      };

      const expandedItinerary = [...itineraryData, newDay];
      const payload = mapItineraryToPayload(expandedItinerary);
      
      await updateFollowupDetails(operationId || targetId, { followup_details: payload });
      showSnackbar("Extra day added successfully", "success");
      setItineraryData(expandedItinerary);
      await fetchOperationData();
    } catch (error) {
      console.error("Failed to add extra day:", error);
      showSnackbar("Failed to add extra day", "error");
    }
  };

  const handleDeleteDay = async (indexToDelete) => {
    try {
      const dayNum = itineraryData[indexToDelete]?.day || indexToDelete + 1;
      if (window.confirm(`Are you sure you want to delete Day ${dayNum}?`)) {
        const updatedItinerary = itineraryData
          .filter((_, idx) => idx !== indexToDelete)
          .map((item, idx) => ({ ...item, day: (idx + 1).toString() }));
        const payload = mapItineraryToPayload(updatedItinerary);
        await updateFollowupDetails(operationId || targetId, { followup_details: payload });
        showSnackbar(`Day ${dayNum} deleted successfully`, "success");
        setItineraryData(updatedItinerary);
        await fetchOperationData();
      }
    } catch (error) {
      console.error("Failed to delete day:", error);
      showSnackbar("Failed to delete day", "error");
    }
  };

  const handleVerifyItineray = () => {
    setVerifyStatus('');
    setRejectedReason('');
    setVerifyPopupOpen(true);
  };

  const handleChangeRequest = async () => {
    if (!description.trim()) {
      showSnackbar("Please enter a description before submitting.", "warning");
      return;
    }
    await addChangeRequest(operationId || targetId, { description });
    setDescription('');
    setChangeRequestOpen(false);
    showSnackbar("Change Request Submitted Successfully", "success");
  };

  const guestFields = [
    { label: 'Name', field: 'name', icon: <PersonIcon fontSize="small" /> },
    { label: 'Contact', field: 'contact', icon: <PhoneIcon fontSize="small" /> },
    { label: 'Email', field: 'email', icon: <EmailIcon fontSize="small" /> },
    { label: 'Pax', field: 'pax', icon: <GroupsIcon fontSize="small" /> },
    { label: 'Hotel', field: 'hotel', icon: <HotelIcon fontSize="small" /> },
    { label: 'Room Type', field: 'roomType', icon: <BedroomParentIcon fontSize="small" /> },
    { label: 'Rooms', field: 'rooms', icon: <BedroomParentIcon fontSize="small" /> },
    { label: 'Car Name', field: 'carname', icon: <DirectionsCarIcon fontSize="small" /> },
    { label: 'Car Count', field: 'carcount', icon: <InfoIcon fontSize="small" /> },
  ];

  const bookingFields = [
    { label: 'Source', field: 'source', icon: <InfoIcon fontSize="small" /> },
    { label: 'Destination', field: 'location', icon: <LocationOnIcon fontSize="small" /> },
    { label: 'Assigned Agent', field: 'assigned_to', icon: <PersonIcon fontSize="small" /> },
    { label: 'Booking Date', field: 'bookingDate', icon: <CalendarMonthIcon fontSize="small" /> },
    { label: 'Tour Date', field: 'tourDate', icon: <CalendarMonthIcon fontSize="small" /> },
    { label: 'Booking Status', field: 'bookingStatus', icon: <VerifiedIcon fontSize="small" /> },
    { label: 'Cost', field: 'cost', icon: <PriceCheckIcon fontSize="small" /> },
    { label: 'Total Paid Amount', field: 'totalPaidAmount', icon: <AccountBalanceWalletIcon fontSize="small" /> },
    { label: 'Due Payment', field: 'duePayment', icon: <AccountBalanceWalletIcon fontSize="small" /> },
  ];

  // TanStack Table instance (kept for compatibility)
  const columns = useMemo(() => [
    ...columnsData.map((col) => ({
      accessorKey: col.field,
      header: col.label,
      cell: ({ row }) => {
        const rowData = row.original;
        const dateFields = ['date', 'checkinDate', 'checkoutDate'];
        return dateFields.includes(col.field)
          ? formatDate(rowData[col.field])
          : rowData[col.field];
      },
    })),
  ], []);

  useReactTable({
    data: itineraryData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!targetId && !fetchSelectedquerie?._id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 4, maxWidth: 500 }}>
          <WarningAmberIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>No Query Selected</Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a query or inquiry from the list to view its details.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/query')} sx={{ mt: 3, borderRadius: 2 }}>
            Back to Queries
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={44} />
          <Typography variant="body1" color="text.secondary" fontWeight="500">Loading query & itinerary details...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 4, maxWidth: 420 }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>{error}</Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={fetchOperationData} sx={{ borderRadius: 2 }}>Retry</Button>
            <Button variant="outlined" onClick={() => navigate('/query')} sx={{ borderRadius: 2 }}>Back to Queries</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Live calculation of due in booking edit drawer
  const liveCost = Number(bookingEditData.cost) || 0;
  const livePaid = Number(bookingEditData.advancePayment) || 0;
  const liveDue = Math.max(0, liveCost - livePaid);

  return (
    <Box p={{ xs: 2, md: 3 }} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header Banner */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2.5, md: 3 }, 
          mb: 3.5, 
          borderRadius: 3.5, 
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', 
          color: 'white',
          boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.3)'
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton 
              onClick={() => navigate('/query')} 
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }, color: 'white' }}
              title="Back to Queries"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.02em', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Query Details
                </Typography>
                {guestInfo.bookingStatus && (
                  <Chip 
                    label={guestInfo.bookingStatus} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontWeight: 'bold' }} 
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.4 }}>
                Overview of guest information, stay logistics, and verified booking status
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }, color: 'white', borderRadius: 2, textTransform: 'none', fontWeight: '600' }}
              onClick={fetchOperationData}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              disabled={pdfLoading}
              startIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <VisibilityOutlinedIcon />}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }, color: 'white', borderRadius: 2, textTransform: 'none', fontWeight: '600' }}
              onClick={() => exportQueryViewPDF({ guestInfo, itineraryData }, { isPreview: true }, setPdfLoading, showSnackbar)}
            >
              Preview PDF
            </Button>
            <Button
              variant="contained"
              disabled={pdfLoading}
              startIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              sx={{ bgcolor: 'white', color: '#1e40af', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: 2, textTransform: 'none', fontWeight: '700' }}
              onClick={() => exportQueryViewPDF({ guestInfo, itineraryData }, { isPreview: false }, setPdfLoading, showSnackbar)}
            >
              Download PDF
            </Button>
            {hasPermission("operation", "change-request") && (
              <Button
                variant="contained"
                sx={{ bgcolor: '#f59e0b', color: 'white', '&:hover': { bgcolor: '#d97706' }, borderRadius: 2, textTransform: 'none', fontWeight: '700' }}
                onClick={() => setChangeRequestOpen(true)}
              >
                Change Request
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Main Info Cards Grid */}
      <Grid container spacing={3}>
        {/* Guest Information Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ borderRadius: 3.5, height: '100%', border: '1px solid #e2e8f0', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <Box sx={{ p: 2, px: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: '#3b82f6', width: 34, height: 34 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Guest & Stay Information</Typography>
                  <Typography variant="caption" color="text.secondary">Contact info, guest head count, and accommodations</Typography>
                </Box>
              </Stack>
              {canEditCards && (
                <Tooltip title="Edit Guest Details">
                  <IconButton 
                    size="small" 
                    onClick={openGuestEditDrawer} 
                    sx={{ color: '#3b82f6', bgcolor: '#eff6ff', '&:hover': { bgcolor: '#dbeafe' }, border: '1px solid #bfdbfe' }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Grid container spacing={2.5}>
                {guestFields.map((item) => {
                  let displayVal = guestInfo[item.field];
                  if (item.field === 'contact') {
                    displayVal = guestInfo.contact ? `${guestInfo.country_code || '+91'} ${guestInfo.contact}` : 'N/A';
                  } else if (item.field === 'pax') {
                    displayVal = `${guestInfo.pax || 0} Adults${guestInfo.kids_above_5 > 0 ? ` + ${guestInfo.kids_above_5} Kids` : ''}`;
                  } else if (!displayVal) {
                    displayVal = 'N/A';
                  }
                  return (
                    <Grid item xs={12} sm={6} key={item.field}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ color: '#64748b', mt: 0.3 }}>{item.icon}</Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="#1e293b" sx={{ wordBreak: 'break-word', mt: 0.2 }}>
                            {displayVal}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Booking Details Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ borderRadius: 3.5, height: '100%', border: '1px solid #e2e8f0', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <Box sx={{ p: 2, px: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: '#10b981', width: 34, height: 34 }}>
                  <CalendarMonthIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Booking & Payment</Typography>
                  <Typography variant="caption" color="text.secondary">Financial breakdown, travel dates, and stage</Typography>
                </Box>
              </Stack>
              {canEditCards && (
                <Tooltip title="Edit Booking Details">
                  <IconButton 
                    size="small" 
                    onClick={openBookingEditDrawer} 
                    sx={{ color: '#10b981', bgcolor: '#ecfdf5', '&:hover': { bgcolor: '#d1fae5' }, border: '1px solid #a7f3d0' }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <CardContent sx={{ p: 2.5 }}>
              <Grid container spacing={2.5}>
                {bookingFields.map((item) => {
                  let displayContent = null;
                  if (item.field === 'bookingDate') {
                    displayContent = (
                      <Typography variant="body2" fontWeight="600" color="#1e293b">
                        {formatDate(guestInfo.bookingDate, true)}
                      </Typography>
                    );
                  } else if (item.field === 'tourDate') {
                    displayContent = (
                      <Typography variant="body2" fontWeight="600" color="#1e293b">
                        {formatDate(guestInfo.tourDate, false)}
                      </Typography>
                    );
                  } else if (item.field === 'assigned_to') {
                    displayContent = (
                      <Typography variant="body2" fontWeight="700" color="primary.main">
                        {guestInfo.assigned_to || 'N/A'}
                      </Typography>
                    );
                  } else if (item.field === 'bookingStatus') {
                    displayContent = getStatusChip(guestInfo[item.field]);
                  } else if (['cost', 'totalPaidAmount', 'duePayment'].includes(item.field)) {
                    const isDue = item.field === 'duePayment';
                    const isPaid = item.field === 'totalPaidAmount';
                    const amount = Number(guestInfo[item.field]) || 0;
                    displayContent = (
                      <Typography 
                        variant="body2" 
                        fontWeight="700" 
                        sx={{ 
                          color: isDue ? (amount > 0 ? '#dc2626' : '#16a34a') : isPaid ? '#16a34a' : '#0f172a',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {formatCurrency(amount)}
                        {isDue && amount === 0 && <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />}
                      </Typography>
                    );
                  } else {
                    displayContent = (
                      <Typography variant="body2" fontWeight="600" color="#1e293b">
                        {guestInfo[item.field] || 'N/A'}
                      </Typography>
                    );
                  }

                  return (
                    <Grid item xs={12} sm={6} key={item.field}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ color: '#64748b', mt: 0.3 }}>{item.icon}</Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="#64748b" fontWeight="700" display="block" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {item.label}
                          </Typography>
                          <Box sx={{ mt: 0.2 }}>
                            {displayContent}
                          </Box>
                        </Box>
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Additional Inclusions & Charges */}
        {guestInfo?.optional_extras && guestInfo.optional_extras.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <Box sx={{ p: 2, px: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: '#8b5cf6', width: 34, height: 34 }}>
                    <AddIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight="700" color="#0f172a">Additional Inclusions & Charges</Typography>
                </Stack>
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  {guestInfo.optional_extras.map((extra, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="700" display="block" sx={{ textTransform: 'uppercase' }}>
                          {extra.name}
                        </Typography>
                        <Typography variant="h6" fontWeight="700" color="primary" sx={{ mt: 0.5 }}>
                          {formatCurrency(extra.price)} {extra.count > 1 ? `(x${extra.count})` : ''}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Short Itinerary Section */}
      <Box sx={{ mt: 5, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              Short Itinerary
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <VerifiedIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                {itineraryData.filter(d => (d.status || '').toLowerCase() === 'approved' || (d.status || '').toLowerCase() === 'confirm').length} of {itineraryData.length} days verified
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ErrorOutlineIcon />}
              onClick={() => setRejectedViewOpen(true)}
              sx={{ borderRadius: 2, fontWeight: '700', textTransform: 'none', px: 2 }}
            >
              View Rejections
            </Button>
            {canEditCards && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddExtraDay}
                sx={{ borderRadius: 2, fontWeight: '700', textTransform: 'none', px: 2.5, boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)' }}
              >
                Add Extra Day
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack spacing={2.5}>
          {itineraryData.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, bgcolor: 'white', border: '2px dashed #cbd5e1' }}>
              <InfoIcon color="disabled" sx={{ fontSize: 44, mb: 1 }} />
              <Typography variant="h6" color="text.secondary" fontWeight="600">No itinerary days scheduled yet.</Typography>
              {canEditCards && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddExtraDay} sx={{ mt: 2, borderRadius: 2 }}>
                  Create Day 1
                </Button>
              )}
            </Paper>
          ) : itineraryData.map((day, index) => (
            <Card 
              key={day.id || index} 
              elevation={0} 
              sx={{ 
                borderRadius: 3.5, 
                border: '1px solid #e2e8f0', 
                bgcolor: 'white',
                transition: 'all 0.2s ease-in-out', 
                '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.06)', borderColor: '#3b82f6' } 
              }}
            >
              <Grid container>
                {/* Day/Date Sidebar */}
                <Grid item xs={12} sm={2.5} md={2} sx={{ bgcolor: '#f8fafc', p: 2.5, borderRight: { sm: '1px solid #e2e8f0' }, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="h4" fontWeight="900" color="primary" sx={{ letterSpacing: '-0.03em' }}>
                    D{day.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 1.5, mt: 0.5 }}>
                    {formatDate(day.date)}
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    {getStatusChip(day.status)}
                  </Box>
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                    <Tooltip title="Edit this day">
                      <IconButton 
                        size="small" 
                        onClick={() => openEditDrawer(day, index)} 
                        sx={{ color: '#3b82f6', bgcolor: '#eff6ff', '&:hover': { bgcolor: '#dbeafe' }, border: '1px solid #bfdbfe' }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEditCards && (
                      <Tooltip title="Delete this day">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteDay(index)} 
                          sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' }, border: '1px solid #fecaca' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Grid>

                {/* Main Content */}
                <Grid item xs={12} sm={9.5} md={10} sx={{ p: 2.5 }}>
                  <Grid container spacing={2.5}>
                    {/* Destination & Title */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon color="error" fontSize="small" />
                        <Typography variant="h6" fontWeight="700" color="#0f172a">
                          {day.place || 'Destination Not Specified'}
                        </Typography>
                      </Stack>
                      <Divider sx={{ my: 1.5, borderColor: '#f1f5f9' }} />
                    </Grid>

                    {/* Hotel Details */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0', height: '100%' }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 32, height: 32 }}>
                            <HotelIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ textTransform: 'uppercase' }}>
                              Accommodation
                            </Typography>
                            <Typography variant="body1" fontWeight="700" color="#1e293b" noWrap>
                              {day.hotelName || 'No Hotel Assigned'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Stay Period</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {day.checkinDate && day.checkoutDate ? `${formatDate(day.checkinDate)} - ${formatDate(day.checkoutDate)}` : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Room Type</Typography>
                            <Typography variant="body2" fontWeight="600">{day.roomType || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Meal Plan</Typography>
                            <Typography variant="body2" fontWeight="600">{getMealPlanLabel(day.mealPlan)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Amount / Rate</Typography>
                            <Typography variant="body2" fontWeight="700" color="success.main">
                              {formatCurrency(day.hotelAmount)}
                            </Typography>
                          </Grid>
                          {day.hotelConfirmation && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Confirmation No.</Typography>
                              <Typography variant="body2" fontWeight="600" color="primary">{day.hotelConfirmation}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Vehicle Details */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0', height: '100%' }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669', width: 32, height: 32 }}>
                            <DirectionsCarIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ textTransform: 'uppercase' }}>
                              Vehicle & Logistics
                            </Typography>
                            <Typography variant="body1" fontWeight="700" color="#1e293b" noWrap>
                              {day.vehicleName || 'No Vehicle Assigned'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Driver Name</Typography>
                            <Typography variant="body2" fontWeight="600">{day.driverName || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Logistics Status</Typography>
                            <Typography variant="body2" fontWeight="600">{day.vehicleStatus || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Vehicle Payment</Typography>
                            <Typography variant="body2" fontWeight="700" color="#1e293b">
                              {formatCurrency(day.vehiclePayment)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          ))}
        </Stack>
      </Box>

      {/* ============================================================== */}
      {/* DRAWER: EDIT GUEST & STAY INFORMATION                           */}
      {/* ============================================================== */}
      <Drawer 
        anchor="right" 
        open={guestDrawer} 
        onClose={() => !guestSubmitting && setGuestDrawer(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, p: 0 } }}
      >
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#3b82f6' }}><PersonIcon /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight="800" color="#0f172a">Edit Guest & Stay</Typography>
              <Typography variant="caption" color="text.secondary">Update guest contact details and hotel preferences</Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setGuestDrawer(false)} disabled={guestSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Stack spacing={3}>
            {/* Section 1: Guest Personal */}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                Guest Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Guest Full Name"
                    value={guestEditData.name}
                    onChange={(e) => setGuestEditData({ ...guestEditData, name: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Code"
                    value={guestEditData.country_code}
                    onChange={(e) => setGuestEditData({ ...guestEditData, country_code: e.target.value })}
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={guestEditData.contact}
                    onChange={(e) => setGuestEditData({ ...guestEditData, contact: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email Address"
                    type="email"
                    value={guestEditData.email}
                    onChange={(e) => setGuestEditData({ ...guestEditData, email: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pax (Adults)"
                    type="number"
                    value={guestEditData.pax}
                    onChange={(e) => setGuestEditData({ ...guestEditData, pax: Number(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kids (Above 5)"
                    type="number"
                    value={guestEditData.kids_above_5}
                    onChange={(e) => setGuestEditData({ ...guestEditData, kids_above_5: Number(e.target.value) || 0 })}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 2: Hotel & Stay */}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                Accommodation Preferences
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={hotels.map(h => h.hotel_name)}
                    value={guestEditData.hotel}
                    onInputChange={(e, val) => setGuestEditData(prev => ({ ...prev, hotel: val }))}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Preferred Hotel Name" 
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <InputAdornment position="start"><HotelIcon fontSize="small" color="action" /></InputAdornment>
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Room Type"
                    value={guestEditData.roomType}
                    onChange={(e) => setGuestEditData({ ...guestEditData, roomType: e.target.value })}
                    placeholder="e.g. Deluxe Mountain View, Suite"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Rooms"
                    type="number"
                    value={guestEditData.rooms}
                    onChange={(e) => setGuestEditData({ ...guestEditData, rooms: Number(e.target.value) || 1 })}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 3: Transportation */}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                Transportation & Logistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Assigned Vehicle Name"
                    value={guestEditData.carname}
                    onChange={(e) => setGuestEditData({ ...guestEditData, carname: e.target.value })}
                    placeholder="e.g. Innova Crysta, Bolero, Sedan"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><DirectionsCarIcon fontSize="small" color="action" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Car Count"
                    type="number"
                    value={guestEditData.carcount}
                    onChange={(e) => setGuestEditData({ ...guestEditData, carcount: Number(e.target.value) || 1 })}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={() => setGuestDrawer(false)} 
            disabled={guestSubmitting}
            sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={saveGuestInfo} 
            disabled={guestSubmitting}
            startIcon={guestSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, px: 4, fontWeight: '700', textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            {guestSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Drawer>

      {/* ============================================================== */}
      {/* DRAWER: EDIT BOOKING & FINANCIAL DETAILS                       */}
      {/* ============================================================== */}
      <Drawer 
        anchor="right" 
        open={bookingDrawer} 
        onClose={() => !bookingSubmitting && setBookingDrawer(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, p: 0 } }}
      >
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#10b981' }}><CalendarMonthIcon /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight="800" color="#0f172a">Edit Booking & Financials</Typography>
              <Typography variant="caption" color="text.secondary">Update status, dates, total package pricing, and advances</Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setBookingDrawer(false)} disabled={bookingSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Stack spacing={3}>
            {/* Section 1: Booking Status & Dates */}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" color="#10b981" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                Booking Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="booking-status-label">Booking Status</InputLabel>
                    <Select
                      labelId="booking-status-label"
                      label="Booking Status"
                      value={bookingEditData.bookingStatus}
                      onChange={(e) => setBookingEditData({ ...bookingEditData, bookingStatus: e.target.value })}
                    >
                      <MenuItem value="Confirm">Confirm (Booked)</MenuItem>
                      <MenuItem value="Upcoming">Upcoming</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Active">Active / On Tour</MenuItem>
                      <MenuItem value="On Hold">On Hold</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="lead-source-label">Lead Source</InputLabel>
                    <Select
                      labelId="lead-source-label"
                      label="Lead Source"
                      value={bookingEditData.source}
                      onChange={(e) => setBookingEditData({ ...bookingEditData, source: e.target.value })}
                    >
                      <MenuItem value="website">Website</MenuItem>
                      <MenuItem value="direct">Direct Inquiry</MenuItem>
                      <MenuItem value="referral">Referral</MenuItem>
                      <MenuItem value="social-media">Social Media</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="agent">Agent / B2B</MenuItem>
                      <MenuItem value="phone">Phone / Call</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Tour Start Date"
                    value={bookingEditData.tourDate}
                    onChange={(e) => setBookingEditData({ ...bookingEditData, tourDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><CalendarMonthIcon fontSize="small" color="action" /></InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 2: Financials */}
            <Box>
              <Typography variant="subtitle2" fontWeight="700" color="#10b981" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                Financial Breakdown
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Total Package Cost"
                    type="number"
                    value={bookingEditData.cost}
                    onChange={(e) => setBookingEditData({ ...bookingEditData, cost: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Advance / Paid Amount"
                    type="number"
                    value={bookingEditData.advancePayment}
                    onChange={(e) => setBookingEditData({ ...bookingEditData, advancePayment: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Live Dynamic Balance Calculator */}
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2.5, 
                      border: '1px solid',
                      borderColor: liveDue === 0 ? '#bbf7d0' : '#fed7aa',
                      bgcolor: liveDue === 0 ? '#f0fdf4' : '#fffbeb'
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                          Calculated Remaining Due
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="800" 
                          sx={{ color: liveDue === 0 ? '#16a34a' : '#ea580c', mt: 0.2 }}
                        >
                          {formatCurrency(liveDue)}
                        </Typography>
                      </Box>
                      <Chip 
                        label={liveDue === 0 ? "Fully Paid" : "Balance Pending"} 
                        size="small"
                        color={liveDue === 0 ? "success" : "warning"}
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={() => setBookingDrawer(false)} 
            disabled={bookingSubmitting}
            sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={saveBookingInfo} 
            disabled={bookingSubmitting}
            startIcon={bookingSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, px: 4, fontWeight: '700', textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
          >
            {bookingSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Drawer>

      {/* ============================================================== */}
      {/* DRAWER: EDIT ITINERARY DAY                                     */}
      {/* ============================================================== */}
      <Drawer 
        anchor="right" 
        open={drawerOpen} 
        onClose={() => !itinerarySubmitting && setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 580 }, p: 0 } }}
      >
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#3b82f6' }}><EditOutlinedIcon /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight="800">
                {canEditCards ? `Edit Day ${editRow.day || ''} Itinerary` : "Change Request for Day"}
              </Typography>
              <Typography variant="caption" color="text.secondary">Modify stay, accommodation, and transport logistics</Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setDrawerOpen(false)} disabled={itinerarySubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={2.5}>
            {/* Journey & Place */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>
                Journey & Destination
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Destination / Sightseeing Route"
                name="place"
                value={editRow.place || ''}
                onChange={handleEditChange}
                placeholder="e.g. Gangtok to Lachung enroute sightseeing"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocationOnIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Journey Date"
                name="date"
                value={editRow.date ? editRow.date.slice(0, 10) : ''}
                onChange={handleEditChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="hotel-season-label">Pricing Season</InputLabel>
                <Select
                  labelId="hotel-season-label"
                  label="Pricing Season"
                  value={hotelSeason}
                  onChange={(e) => setHotelSeason(e.target.value)}
                >
                  <MenuItem value="off_season_price">Off Season</MenuItem>
                  <MenuItem value="mid_season_price">Mid Season</MenuItem>
                  <MenuItem value="peak_season_price">Peak Season</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Accommodation Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, mt: 1 }}>
                Accommodation Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                size="small"
                freeSolo
                options={hotels}
                getOptionLabel={(option) => typeof option === 'string' ? option : `${option.hotel_name} (${option.sub_destination || option.location || 'N/A'})`}
                value={hotels.find(h => h._id === editRow.hotelId) || editRow.hotelName || null}
                onInputChange={(e, newInputValue) => {
                  if (!hotels.find(h => h.hotel_name === newInputValue)) {
                    setEditRow(prev => ({ ...prev, hotelId: '', hotelName: newInputValue }));
                  }
                }}
                onChange={(e, value) => {
                  if (typeof value === 'string') {
                    setEditRow(prev => ({ ...prev, hotelId: '', hotelName: value, roomType: '', mealPlan: '' }));
                    setRoomOptions([]);
                    return;
                  }
                  const updatedRow = {
                    ...editRow,
                    hotelId: value?._id || '',
                    hotelName: value?.hotel_name || '',
                    roomType: '',
                    mealPlan: ''
                  };
                  const cost = calculateSingleHotelCost(
                    { hotelId: updatedRow.hotelId, roomType: updatedRow.roomType, mealPlan: updatedRow.mealPlan },
                    hotels,
                    hotelSeason,
                    tripDetails,
                    stayInfo
                  );
                  setEditRow({ ...updatedRow, hotelAmount: cost || 0 });
                  setRoomOptions(value?.category || []);
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  const priced = hasHotelPrice(option._id);
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}
                      sx={{
                        backgroundColor: priced ? '#f0fdf4 !important' : 'inherit',
                        '&:hover': {
                          backgroundColor: priced ? '#dcfce7 !important' : '#f1f5f9 !important',
                        }
                      }}
                    >
                      {option.hotel_name} ({option.sub_destination || option.location || 'N/A'})
                    </Box>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="Hotel Name" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                size="small"
                freeSolo
                options={roomOptions.map(cat => cat.room_cat)}
                value={editRow.roomType || ''}
                onInputChange={(e, newInputValue) => {
                  setEditRow(prev => ({ ...prev, roomType: newInputValue }));
                }}
                onChange={(e, value) => {
                  const updatedRow = { ...editRow, roomType: value || '' };
                  const cost = calculateSingleHotelCost(
                    { hotelId: updatedRow.hotelId, roomType: updatedRow.roomType, mealPlan: updatedRow.mealPlan },
                    hotels,
                    hotelSeason,
                    tripDetails,
                    stayInfo
                  );
                  setEditRow({ ...updatedRow, hotelAmount: cost || editRow.hotelAmount || 0 });
                }}
                renderInput={(params) => <TextField {...params} label="Room Type" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="meal-plan-edit-label">Meal Plan</InputLabel>
                <Select
                  labelId="meal-plan-edit-label"
                  label="Meal Plan"
                  value={editRow.mealPlan || ''}
                  onChange={(e) => {
                    const planVal = e.target.value;
                    const updatedRow = { ...editRow, mealPlan: planVal };
                    const cost = calculateSingleHotelCost(
                      { hotelId: updatedRow.hotelId, roomType: updatedRow.roomType, mealPlan: planVal },
                      hotels,
                      hotelSeason,
                      tripDetails,
                      stayInfo
                    );
                    setEditRow({ ...updatedRow, hotelAmount: cost || editRow.hotelAmount || 0 });
                  }}
                >
                  <MenuItem value="">None / Not Selected</MenuItem>
                  <MenuItem value="cp_plan">CP (Breakfast)</MenuItem>
                  <MenuItem value="map_plan">MAP (Bf + 1 Meal)</MenuItem>
                  <MenuItem value="ap_plan">AP (All Meals)</MenuItem>
                  <MenuItem value="ep_plan">EP (No Meals)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hotel Amount / Cost"
                type="number"
                name="hotelAmount"
                value={editRow.hotelAmount ?? ''}
                onChange={handleEditChange}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hotel Confirmation No."
                name="hotelConfirmation"
                value={editRow.hotelConfirmation || ''}
                onChange={handleEditChange}
                placeholder="Booking reference"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><ConfirmationNumberIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Checkin Date"
                name="checkinDate"
                value={editRow.checkinDate ? editRow.checkinDate.slice(0, 10) : ''}
                onChange={handleEditChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Checkout Date"
                name="checkoutDate"
                value={editRow.checkoutDate ? editRow.checkoutDate.slice(0, 10) : ''}
                onChange={handleEditChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Vehicle & Logistics */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight="700" color="#3b82f6" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, mt: 1 }}>
                Vehicle & Driver Logistics
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle Name"
                name="vehicleName"
                value={editRow.vehicleName || ''}
                onChange={handleEditChange}
                placeholder="e.g. North Bolero, Sedan, Innova"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><DirectionsCarIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Driver Name"
                name="driverName"
                value={editRow.driverName || ''}
                onChange={handleEditChange}
                placeholder="Assigned driver"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" color="action" /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle Payment"
                type="number"
                name="vehiclePayment"
                value={editRow.vehiclePayment ?? ''}
                onChange={handleEditChange}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="vehicle-status-label">Vehicle Status</InputLabel>
                <Select
                  labelId="vehicle-status-label"
                  label="Vehicle Status"
                  name="vehicleStatus"
                  value={editRow.vehicleStatus || ''}
                  onChange={handleEditChange}
                >
                  <MenuItem value="">Not Specified</MenuItem>
                  <MenuItem value="Assigned">Assigned</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Enroute">Enroute</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            onClick={() => setDrawerOpen(false)} 
            disabled={itinerarySubmitting}
            sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
          >
            Cancel
          </Button>
          {hasPermission("operation", "verify") && (
            <Button 
              variant="outlined" 
              color="success" 
              onClick={handleVerifyItineray} 
              disabled={itinerarySubmitting}
              sx={{ borderRadius: 2, px: 2.5, textTransform: 'none', fontWeight: 'bold' }}
            >
              Verify Day
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary" 
            onClick={saveEdit} 
            disabled={itinerarySubmitting}
            startIcon={itinerarySubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            sx={{ borderRadius: 2, px: 4, fontWeight: '700', textTransform: 'none' }}
          >
            {itinerarySubmitting ? 'Saving...' : (hasPermission("operation", "alter") || canEditCards ? "Save Changes" : "Submit Request")}
          </Button>
        </Box>
      </Drawer>

      {/* Global Snackbar */}
      <SnackbarComponent />

      {/* Change Request Dialog */}
      <Dialog open={changeRequestOpen} onClose={() => setChangeRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: '800' }}>Submit Change Request</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe the required modifications for this query or itinerary in detail. The operation manager will review and approve.
          </Typography>
          <TextField
            label="Change Description"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Please update guest hotel to Hotel Doma Residency and increase pax to 4."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChangeRequestOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleChangeRequest} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog open={verifyPopupOpen} onClose={() => setVerifyPopupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: '800' }}>
          Verify Itinerary (Day {editIndex !== null && itineraryData[editIndex] ? itineraryData[editIndex].day : ''})
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Review Status</Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1, mb: 2 }}>
            <Button
              variant={verifyStatus === 'Approved' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => setVerifyStatus('Approved')}
              sx={{ flex: 1, textTransform: 'none', fontWeight: 'bold' }}
            >
              Approve
            </Button>
            <Button
              variant={verifyStatus === 'Rejected' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setVerifyStatus('Rejected')}
              sx={{ flex: 1, textTransform: 'none', fontWeight: 'bold' }}
            >
              Reject
            </Button>
          </Stack>

          {verifyStatus === 'Rejected' && (
            <TextField
              label="Rejection Reason"
              fullWidth
              multiline
              rows={3}
              value={rejectedReason}
              onChange={(e) => setRejectedReason(e.target.value)}
              placeholder="State reason why this day's arrangement was rejected"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVerifyPopupOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!verifyStatus || (verifyStatus === 'Rejected' && !rejectedReason.trim())}
            onClick={async () => {
              const updatedItinerary = [...itineraryData];
              const item = editIndex !== null ? updatedItinerary[editIndex] : null;

              if (!item) {
                showSnackbar("Error: No item selected for verification", "error");
                return;
              }

              item.status = verifyStatus;
              if (verifyStatus === 'Rejected') {
                item.rejected_reason = item.rejected_reason || [];
                item.rejected_reason.push({ description: rejectedReason, date_time: new Date() });
              }

              const res = await updateFollowupDetails(operationId || targetId, {
                followup_details: mapItineraryToPayload(updatedItinerary)
              });

              if (res) {
                setItineraryData(updatedItinerary);
                showSnackbar(`Day ${item.day} marked as ${verifyStatus}`, "success");
                setVerifyPopupOpen(false);
                setDrawerOpen(false);
              }
            }}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejected Reasons Dialog */}
      <Dialog open={rejectedViewOpen} onClose={() => setRejectedViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: '800' }}>All Rejected Reasons</DialogTitle>
        <DialogContent dividers>
          {itineraryData.filter(day => Array.isArray(day.rejected_reason) && day.rejected_reason.length > 0).length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No rejected reasons recorded for this itinerary.
            </Alert>
          ) : (
            itineraryData.map((day, idx) =>
              Array.isArray(day.rejected_reason) && day.rejected_reason.length > 0 ? (
                <Box key={idx} sx={{ mb: 2.5, p: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2.5 }}>
                  <Typography fontWeight="bold" color="#991b1b" gutterBottom>
                    Day {day.day} - {formatDate(day.date)} - {day.place || 'Destination'}
                  </Typography>
                  {day.rejected_reason.map((entry, i) => (
                    <Typography variant="body2" key={i} color="#7f1d1d" sx={{ pl: 1.5, mt: 0.5 }}>
                      • {new Date(entry.date_time || Date.now()).toLocaleString()}: {entry.description}
                    </Typography>
                  ))}
                </Box>
              ) : null
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectedViewOpen(false)} color="primary" variant="contained" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SingleQueriesView;
